import {
	CardType,
	ControlType,
	type FilterCardConfig,
} from '@framework/analytics/models/card-model';
import {
	type CardList,
	DATE_RANGE_FILTER_HEADER_ONE_LINE_LAYOUT_H,
	DATE_RANGE_FILTER_HEADER_TWO_LINES_LAYOUT_H,
	DROPDOWN_FILTER_LAYOUT_H,
	DROPDOWN_FILTER_LAYOUT_MIN_H,
} from '@framework/analytics/models/layout.model';

/** Card header text that changes a date range filter's row span. */
export type FilterCardHeaderText = Pick<FilterCardConfig, 'title' | 'subtitle'>;

export interface ResolveFilterControlTypeInput {
	layout?: Pick<CardList, 'controlType' | 'cardId'>;
	config?: Pick<FilterCardConfig, 'controlType' | 'cardId'>;
}

/**
 * Resolves filter UI from persisted {@link FilterCardConfig.controlType} and layout
 * {@link CardList.controlType} only. Runtime WebSocket payload must not change control type.
 *
 * @throws When neither config nor layout provides `controlType`.
 */
export function resolveFilterControlType(
	input: ResolveFilterControlTypeInput
): ControlType {
	const { layout, config } = input;

	if (config?.controlType) {
		return config.controlType;
	}

	if (layout?.controlType) {
		return layout.controlType;
	}

	const cardId = config?.cardId ?? layout?.cardId;
	const cardPart = cardId ? ` (cardId=${cardId})` : '';
	throw new Error(
		`Filter card controlType is required${cardPart}. Set controlType on FilterCardConfig or CardList.`
	);
}

export function isDropdownFilterControlType(
	controlType: ControlType | null
): boolean {
	return controlType === ControlType.Dropdown;
}

export function isSearchFilterControlType(
	controlType: ControlType | null
): boolean {
	return controlType === ControlType.Search;
}

/** Dropdown, search and date-range filters are single-control tiles on the report grid. */
export function isCompactFilterControlType(
	controlType: ControlType | null | undefined
): boolean {
	return (
		controlType === ControlType.Dropdown ||
		controlType === ControlType.Search ||
		controlType === ControlType.DateRange
	);
}

/** Number of header lines (title, description) a date range filter card shows. */
export function dateRangeFilterHeaderLines(
	header: FilterCardHeaderText | null | undefined
): number {
	return [header?.title, header?.subtitle].filter(text => !!text?.trim())
		.length;
}

/**
 * Fixed GridStack row span for compact filter tiles (vertical resize disabled).
 * Date range filters grow when they show a title and/or description.
 */
export function compactFilterLayoutRowHeight(
	controlType: ControlType | null | undefined,
	header?: FilterCardHeaderText | null
): number | null {
	if (!isCompactFilterControlType(controlType)) {
		return null;
	}
	if (controlType !== ControlType.DateRange) {
		return DROPDOWN_FILTER_LAYOUT_H;
	}
	switch (dateRangeFilterHeaderLines(header)) {
		case 0:
			return DROPDOWN_FILTER_LAYOUT_H;
		case 1:
			return DATE_RANGE_FILTER_HEADER_ONE_LINE_LAYOUT_H;
		default:
			return DATE_RANGE_FILTER_HEADER_TWO_LINES_LAYOUT_H;
	}
}

/** GridStack `minH` for compact filter tiles. */
export function compactFilterLayoutMinRowHeight(
	controlType: ControlType | null | undefined
): number | null {
	return isCompactFilterControlType(controlType)
		? DROPDOWN_FILTER_LAYOUT_MIN_H
		: null;
}

/** Normalizes persisted layout rows for compact filter cards. */
export function normalizeCompactFilterLayoutCard(
	card: CardList,
	controlType?: ControlType | null,
	header?: FilterCardHeaderText | null
): CardList {
	const resolved = controlType ?? card.controlType;
	const h = compactFilterLayoutRowHeight(resolved, header);
	const minH = compactFilterLayoutMinRowHeight(resolved);
	if (card.cardType !== CardType.Filter || h === null || minH === null) {
		return card;
	}

	return {
		...card,
		controlType: resolved ?? undefined,
		h,
		minH,
	};
}
